import React, { useEffect, useState } from "react";
import {
  Button,
  ShowProps,
  TextField,
  useDataProvider,
  useRecordContext,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import {
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { GridinRowsWithSpace } from "utils/style";
import CustomEmptyComponent from "components/CustomEmptyComponent";
import CustomPriceField from "../../components/CustomPriceField";
import moment from "moment/moment";
import CustomLabeled from "../../components/layout/CustomLabeled";

interface IItem {
  subcontractorAreaId: number;
  area: string;
  feeVehicleSizeSmall: number;
  feeVehicleSizeMedium: number;
  feeVehicleSizeLarge: number;
}

interface ISchoolIncentive {
  subcontractorAreaId: number;
  area: string;
  feeVehicleSizeSmall: number;
  feeVehicleSizeMedium: number;
  feeVehicleSizeLarge: number;
}

const useStyles = makeStyles({
  summaryButton: {
    position: "relative",
    top: 20,
  },
});

const SubcontractorRateChartShow = (props: ShowProps) => {
  const provider = useDataProvider();
  const record = useRecordContext();

  const classes = useStyles();

  const [areas, setAreas] = useState<ISubcontractorAreas[]>([]);
  const [subcontractorRates, setSubcontractorRates] = useState<
    ISubcontractorRates[]
  >([]);
  const [subcontractorRateChart, setSubcontractorRateChart] =
    useState<ISubcontractorRateCharts>();
  const [regularItems, setRegularItems] = useState<IItem[] | []>([]);
  const [ccaItems, setCcaItems] = useState<IItem[] | []>([]);
  const [additionalIncentiveRates, setAdditionalIncentiveRates] =
    useState<IAdditionalIncentive>();
  const [capacityIncentiveRates, setCapacityIncentiveRates] = useState<
    ICapacityIncentive[] | []
  >([]);

  const [schoolIncentives, setSchoolIncentives] = useState<
    ISchoolIncentive[] | []
  >([]);

  useEffect(() => {
    provider
      .getList("subcontractorRateCharts", {
        filter: {
          SchoolId: record.id,
        },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "",
          order: "DESC",
        },
      })
      .then((result: any) => {
        if (result == null) {
          //reload page if the fetching data is undefined or null
          window.location.reload();
        }

        setSubcontractorRates(result.data);
      });

    provider
      .getList("subcontractorAreas", {
        filter: {
          SchoolId: record.id,
        },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "maxDistanceInMeters",
          order: "ASC",
        },
      })
      .then((result: any) => {
        result.data
          .sort((a: any, b: any) => {
            return a.maxDistanceInMeters - b.maxDistanceInMeters;
          })
          .map((record: any, index: number) => {
            const item = record;
            if (item) {
              item.order = index + 1;
            }
          });

        setAreas(result.data);
      });
  }, []);

  useEffect(() => {
    if (subcontractorRates && subcontractorRates.length !== 0) {
      provider
        .getOne(`subcontractorRateCharts`, {
          id: subcontractorRates[0].id,
        })
        .then((result: any) => {
          setSubcontractorRateChart(result.data);
          setAdditionalIncentiveRates(result.data.additionalIncentiveRates);
        });
    }
  }, [subcontractorRates]);

  useEffect(() => {
    if (
      areas.length > 0 &&
      subcontractorRateChart?.items &&
      subcontractorRateChart.items.length > 0
    ) {
      const rowDataRegular: IItem[] = [];
      const rowDataCca: IItem[] = [];
      areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
        const data: IItem = {
          area: `Area ${order} (${maxDistanceInMeters}m)`,
          subcontractorAreaId: id,
          feeVehicleSizeLarge: 0,
          feeVehicleSizeMedium: 0,
          feeVehicleSizeSmall: 0,
        };
        rowDataRegular.push(data);
        rowDataCca.push({ ...data }); // Create a shallow copy of data for rowDataCca
      });

      const regularRateChartItems = subcontractorRateChart.items.filter(
        (item) => item.rateChartItemType === "regular",
      );
      regularRateChartItems.forEach((item) => {
        let area = rowDataRegular.findIndex(
          (data) => data.subcontractorAreaId === item.subcontractorAreaId,
        );
        if (item.vehicleSize === "small") {
          rowDataRegular[area].feeVehicleSizeSmall = item.fee / 100;
        } else if (item.vehicleSize === "medium") {
          rowDataRegular[area].feeVehicleSizeMedium = item.fee / 100;
        } else if (item.vehicleSize === "large") {
          rowDataRegular[area].feeVehicleSizeLarge = item.fee / 100;
        }
      });
      setRegularItems(rowDataRegular);

      const ccaRateChartItems = subcontractorRateChart.items.filter(
        (item) => item.rateChartItemType === "cca",
      );
      ccaRateChartItems.forEach((item) => {
        let area = rowDataCca.findIndex(
          (data) => data.subcontractorAreaId === item.subcontractorAreaId,
        );
        if (item.vehicleSize === "small") {
          rowDataCca[area].feeVehicleSizeSmall = item.fee / 100;
        } else if (item.vehicleSize === "medium") {
          rowDataCca[area].feeVehicleSizeMedium = item.fee / 100;
        } else if (item.vehicleSize === "large") {
          rowDataCca[area].feeVehicleSizeLarge = item.fee / 100;
        }
      });
      setCcaItems(rowDataCca);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorRateChart, areas]);

  useEffect(() => {
    if (
      areas.length > 0 &&
      subcontractorRateChart?.capacityIncentiveRates &&
      subcontractorRateChart.capacityIncentiveRates.length > 0
    ) {
      let rowData: ICapacityIncentive[] = [];
      areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
        const data: ICapacityIncentive = {
          area: `Area ${order} (${maxDistanceInMeters}m)`,
          subcontractorAreaId: id,
          ratePerStudent: 0,
        };
        rowData.push(data);
      });

      subcontractorRateChart.capacityIncentiveRates.forEach(
        (capacityIncentiveRate) => {
          let areaIndex = rowData.findIndex(
            (data) =>
              data.subcontractorAreaId ===
              capacityIncentiveRate.subcontractorAreaId,
          );
          if (areaIndex !== -1) {
            rowData[areaIndex].ratePerStudent =
              capacityIncentiveRate.ratePerStudent / 100;
          }
        },
      );
      setCapacityIncentiveRates(rowData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorRateChart, areas]);

  useEffect(() => {
    if (
      areas.length > 0 &&
      subcontractorRateChart?.schoolIncentives &&
      subcontractorRateChart.schoolIncentives.length > 0
    ) {
      let rowData: ISchoolIncentive[] = [];
      areas.forEach(({ order, id, maxDistanceInMeters }: any) => {
        const data: ISchoolIncentive = {
          area: `Area ${order} (${maxDistanceInMeters}m)`,
          subcontractorAreaId: id,
          feeVehicleSizeLarge: 0,
          feeVehicleSizeMedium: 0,
          feeVehicleSizeSmall: 0,
        };
        rowData.push(data);
      });

      subcontractorRateChart.schoolIncentives.forEach((item) => {
        let area = rowData.findIndex(
          (data) => data.subcontractorAreaId === item.subcontractorAreaId,
        );
        if (item.vehicleSize === "small") {
          rowData[area].feeVehicleSizeSmall = item.fee / 100;
        } else if (item.vehicleSize === "medium") {
          rowData[area].feeVehicleSizeMedium = item.fee / 100;
        } else if (item.vehicleSize === "large") {
          rowData[area].feeVehicleSizeLarge = item.fee / 100;
        }
      });
      setSchoolIncentives(rowData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcontractorRateChart, areas]);

  return (
    <SanitizedGrid container spacing={2}>
      <Grid item xs={12} style={GridinRowsWithSpace}>
        <Typography variant="h6">Subcontractor Rate Charts</Typography>

        {subcontractorRateChart && (
          <Button
            className={classes.summaryButton}
            onClick={() =>
              props.history.push(
                `/subcontractorRateCharts/${
                  subcontractorRateChart.id
                }?schoolId=${Number(record.id)}`,
              )
            }
            label="Edit"
          >
            <EditIcon />
          </Button>
        )}
      </Grid>

      {subcontractorRateChart ? (
        <>
          <Grid item xs={4}>
            <CustomLabeled name="Name">
              <TextField
                defaultValue={subcontractorRateChart?.name || "-"}
                emptyText={subcontractorRateChart?.name || "-"}
              />
            </CustomLabeled>
          </Grid>
          <Grid item xs={4}>
            <CustomLabeled name="Last Updated At">
              <Typography variant="body2">
                {subcontractorRateChart?.updatedAt === undefined ||
                subcontractorRateChart?.updatedAt === null
                  ? `-`
                  : moment
                      .utc(subcontractorRateChart?.updatedAt)
                      .format("DD / MM / YYYY, hh:mm A")}
              </Typography>
            </CustomLabeled>
          </Grid>
          <Grid item xs={4}></Grid>

          <Grid item xs={12}>
            <CustomLabeled name="Bus Assistant Incentive">
              <CustomPriceField
                source="busAssistantIncentive"
                record={subcontractorRateChart}
              />
            </CustomLabeled>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">
              Regular Subcontractor Rate Charts Items
            </Typography>
          </Grid>

          {regularItems.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Area</TableCell>
                    <TableCell>Vehicle Size Small</TableCell>
                    <TableCell>Vehicle Size Medium</TableCell>
                    <TableCell>Vehicle Size Large</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regularItems.map((item: IItem) => (
                    <TableRow key={`regular-${item.subcontractorAreaId}`}>
                      <TableCell component="th" scope="row">
                        {item.area}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        $ {item.feeVehicleSizeSmall}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        $ {item.feeVehicleSizeMedium}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        $ {item.feeVehicleSizeLarge}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1">
                Regular Subcontractor Rate Charts Items is Empty.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} style={{ marginTop: 40 }}>
            <Typography variant="subtitle2">
              CCA Subcontractor Rate Charts Items
            </Typography>
          </Grid>

          {ccaItems.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Area</TableCell>
                    <TableCell>Vehicle Size Small</TableCell>
                    <TableCell>Vehicle Size Medium</TableCell>
                    <TableCell>Vehicle Size Large</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ccaItems.map((item: IItem) => (
                    <TableRow key={`cca-${item.subcontractorAreaId}`}>
                      <TableCell component="th" scope="row">
                        {item.area}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        $ {item.feeVehicleSizeSmall}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        $ {item.feeVehicleSizeMedium}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        $ {item.feeVehicleSizeLarge}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1">
                CCA Subcontractor Rate Charts Items is Empty.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} style={{ marginTop: 40 }}>
            <Typography variant="subtitle2">Incentive Threshold</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1" gutterBottom>
              Additional Incentive
            </Typography>
            <CustomLabeled name="Small Vehicle">
              <TextField
                defaultValue={
                  `${subcontractorRateChart.smallVehicleAdditionalIncentiveThreshold} student` ||
                  "-"
                }
                emptyText={
                  `${subcontractorRateChart.smallVehicleAdditionalIncentiveThreshold} student` ||
                  "-"
                }
              />
            </CustomLabeled>
            <CustomLabeled name="Medium Vehicle">
              <TextField
                defaultValue={
                  `${subcontractorRateChart.mediumVehicleAdditionalIncentiveThreshold} student` ||
                  "-"
                }
                emptyText={
                  `${subcontractorRateChart.mediumVehicleAdditionalIncentiveThreshold} student` ||
                  "-"
                }
              />
            </CustomLabeled>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" gutterBottom>
              Capacity Incentive
            </Typography>
            <CustomLabeled name="Medium Vehicle">
              <TextField
                defaultValue={
                  `${subcontractorRateChart.mediumVehicleCapacityIncentiveThreshold} student` ||
                  "-"
                }
                emptyText={
                  `${subcontractorRateChart.mediumVehicleCapacityIncentiveThreshold} student` ||
                  "-"
                }
              />
            </CustomLabeled>
            <CustomLabeled name="Large Vehicle">
              <TextField
                defaultValue={
                  `${subcontractorRateChart.largeVehicleCapacityIncentiveThreshold} student` ||
                  "-"
                }
                emptyText={
                  `${subcontractorRateChart.largeVehicleCapacityIncentiveThreshold} student` ||
                  "-"
                }
              />
            </CustomLabeled>
          </Grid>

          <Grid item xs={12} style={{ marginTop: 40 }}>
            <Typography variant="subtitle2">
              Additional Incentive Rates
            </Typography>
          </Grid>

          {additionalIncentiveRates ? (
            <Grid item xs={7}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehicle Size</TableCell>
                      <TableCell>Incentive</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow key={`${additionalIncentiveRates}smallVehicle`}>
                      <TableCell component="th" scope="row">
                        Small Vehicle
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {additionalIncentiveRates.smallVehicleFee
                          ? `$ ${
                              additionalIncentiveRates.smallVehicleFee.toFixed(
                                2,
                              ) / 100
                            }`
                          : `-`}
                      </TableCell>
                    </TableRow>
                    <TableRow key={`${additionalIncentiveRates}mediumVehicle`}>
                      <TableCell component="th" scope="row">
                        Medium Vehicle
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {additionalIncentiveRates.mediumVehicleFee
                          ? `$ ${
                              additionalIncentiveRates.mediumVehicleFee.toFixed(
                                2,
                              ) / 100
                            }`
                          : `-`}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1">
                Additional incentive rates is Empty.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} style={{ marginTop: 40 }}>
            <Typography variant="subtitle2">
              Capacity Incentive Rate Charts Items
            </Typography>
          </Grid>

          {capacityIncentiveRates.length > 0 ? (
            <Grid item xs={7}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Area</TableCell>
                      <TableCell>Rate Per Student</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {capacityIncentiveRates.map((item: ICapacityIncentive) => (
                      <TableRow key={`${item.subcontractorAreaId}`}>
                        <TableCell component="th" scope="row">
                          {item.area}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          $ {item.ratePerStudent}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1">
                Capacity Incentive Rate Charts Items is Empty.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} style={{ marginTop: 40 }}>
            <Typography variant="subtitle2">School Incentives</Typography>
          </Grid>

          {schoolIncentives.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Area</TableCell>
                      <TableCell>Vehicle Size Small</TableCell>
                      <TableCell>Vehicle Size Medium</TableCell>
                      <TableCell>Vehicle Size Large</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schoolIncentives.map((item: IItem) => (
                      <TableRow key={`${item.subcontractorAreaId}`}>
                        <TableCell component="th" scope="row">
                          {item.area}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          $ {item.feeVehicleSizeSmall}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          $ {item.feeVehicleSizeMedium}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          $ {item.feeVehicleSizeLarge}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1">
                School Incentives is Empty.
              </Typography>
            </Grid>
          )}
        </>
      ) : (
        <CustomEmptyComponent
          resource={`subcontractorRateCharts`}
          optionalQuery={`?source=${JSON.stringify({
            schoolId: Number(record.id),
          })}&schoolId=${Number(record.id)}`}
          hasCreate={true}
        />
      )}
    </SanitizedGrid>
  );
};
export default SubcontractorRateChartShow;
